import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getOrganizationForPrint } from './organizationHelper';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

export const generatePropertyPDF = async (property, propertyDetails = null, organization = null) => {
  const data = propertyDetails || property;

  // Get current language from localStorage or default to 'en'
  const currentLang = localStorage.getItem('language') || 'en';
  const isArabic = currentLang === 'ar';

  // Get organization details if not provided
  if (!organization) {
    organization = await getOrganizationForPrint();
  }

  // Helper to get organization field with fallback
  const getOrgField = (field, fallback = '') => {
    if (!organization) return fallback;
    return organization[field] || organization[field.charAt(0).toUpperCase() + field.slice(1)] || fallback;
  };

  // Get logo URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
  const baseUrl = API_BASE_URL.replace('/api', '');
  const logoPath = getOrgField('logo');
  const logoUrl = logoPath ? `${baseUrl}${logoPath}` : '/logo.png';
  const orgNameAr = getOrgField('organizationNameAr', 'عقار لايف');
  const orgNameEng = getOrgField('organizationNameEng', 'Agar Live');

  const printDate = new Date().toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const propertyCode = data.code || data.Code || 'N/A';

  // Language labels
  const t = isArabic ? {
    propertyDetails: 'تفاصيل العقار',
    propertyInfo: 'معلومات العقار',
    propertyId: 'رقم العقار',
    propertyCode: 'رمز العقار',
    nameEng: 'الاسم (الإنجليزية)',
    nameAr: 'الاسم (العربية)',
    ownerDocType: 'نوع وثيقة المالك',
    titleDeed: 'صك الملكية',
    identityNumber: 'رقم الهوية',
    crNumber: 'رقم السجل التجاري',
    issueDate: 'تاريخ الإصدار',
    placeTypeCity: 'نوع المكان (المدينة)',
    ownershipInfo: 'معلومات الملكية',
    dateOfBirth: 'تاريخ الميلاد',
    nationality: 'الجنسية',
    identityType: 'نوع الهوية',
    mobile: 'الجوال',
    email: 'البريد الإلكتروني',
    bank: 'البنك',
    bankAccount: 'رقم الحساب البنكي',
    agentNumber: 'رقم الوكيل',
    address: 'العنوان',
    propertyDetailsSection: 'تفاصيل العقار',
    municipalLicenseNumber: 'رقم الترخيص البلدي',
    realEstateType: 'نوع العقار',
    realEstateStatus: 'حالة العقار',
    realEstateUsage: 'استخدام العقار',
    realEstateNumber: 'رقم العقار',
    unitFloor: 'طابق الوحدة',
    realEstateAge: 'عمر العقار',
    years: 'سنوات',
    totalUnits: 'إجمالي الوحدات',
    totalFloors: 'إجمالي الطوابق',
    city: 'المدينة',
    area: 'المنطقة',
    district: 'الحي',
    streetName: 'اسم الشارع',
    buildingNumber: 'رقم المبنى',
    additionalNumber: 'الرقم الإضافي',
    postalCode: 'الرمز البريدي',
    isPartOfCompound: 'جزء من مجمع',
    yes: 'نعم',
    no: 'لا',
    propertyFacilities: 'مرافق ومزايا العقار',
    unitsDetails: 'تفاصيل الوحدات',
    unitType: 'نوع الوحدة',
    unitUsage: 'استخدام الوحدة',
    floorNumber: 'رقم الطابق',
    unitArea: 'مساحة الوحدة',
    numberOfRooms: 'عدد الغرف',
    establishedDate: 'تاريخ الإنشاء',
    electricityMeter: 'عداد الكهرباء',
    waterMeter: 'عداد المياه',
    gasMeter: 'عداد الغاز',
    parkingSpaces: 'مواقف السيارات',
    unitFacilities: 'مرافق الوحدة',
    noFacilities: 'لا توجد مرافق',
    noOwnership: 'لا توجد معلومات ملكية',
    noUnits: 'لا توجد وحدات',
    agent: 'وكيل',
    owner: 'مالك',
    unit: 'الوحدة',
    quantity: 'الكمية',
    printDate: 'تاريخ الطباعة',
    page: 'صفحة',
    of: 'من',
    systemName: 'نظام إدارة العقارات'
  } : {
    propertyDetails: 'Property Details',
    propertyInfo: 'Property Information',
    propertyId: 'Property ID',
    propertyCode: 'Property Code',
    nameEng: 'Name (English)',
    nameAr: 'Name (Arabic)',
    ownerDocType: 'Owner Document Type',
    titleDeed: 'Title Deed',
    identityNumber: 'Identity Number',
    crNumber: 'CR Number',
    issueDate: 'Issue Date',
    placeTypeCity: 'Place Type (City)',
    ownershipInfo: 'Ownership Information',
    dateOfBirth: 'Date of Birth',
    nationality: 'Nationality',
    identityType: 'Identity Type',
    mobile: 'Mobile',
    email: 'Email',
    bank: 'Bank',
    bankAccount: 'Bank Account',
    agentNumber: 'Agent Number',
    address: 'Address',
    propertyDetailsSection: 'Property Details',
    municipalLicenseNumber: 'Municipal License Number',
    realEstateType: 'Real Estate Type',
    realEstateStatus: 'Real Estate Status',
    realEstateUsage: 'Real Estate Usage',
    realEstateNumber: 'Real Estate Number',
    unitFloor: 'Unit Floor',
    realEstateAge: 'Real Estate Age',
    years: 'years',
    totalUnits: 'Total Units',
    totalFloors: 'Total Floors',
    city: 'City',
    area: 'Area',
    district: 'District',
    streetName: 'Street Name',
    buildingNumber: 'Building Number',
    additionalNumber: 'Additional Number',
    postalCode: 'Postal Code',
    isPartOfCompound: 'Is Part of Compound',
    yes: 'Yes',
    no: 'No',
    propertyFacilities: 'Property Facilities and Amenities',
    unitsDetails: 'Units Details',
    unitType: 'Unit Type',
    unitUsage: 'Unit Usage',
    floorNumber: 'Floor Number',
    unitArea: 'Unit Area',
    numberOfRooms: 'Number of Rooms',
    establishedDate: 'Established Date',
    electricityMeter: 'Electricity Meter',
    waterMeter: 'Water Meter',
    gasMeter: 'Gas Meter',
    parkingSpaces: 'Parking Spaces',
    unitFacilities: 'Unit Facilities',
    noFacilities: 'No facilities listed',
    noOwnership: 'No ownership information available',
    noUnits: 'No units added',
    agent: 'Agent',
    owner: 'Owner',
    unit: 'Unit',
    quantity: 'Quantity',
    printDate: 'Print Date',
    page: 'Page',
    of: 'of',
    systemName: 'Property Management System'
  };

  const ownerDocumentTypeMap = isArabic ? { 1: 'صك إلكتروني', 2: 'صك سجل عقاري' } : { 1: 'Electronic Title Deed', 2: 'Property Registry Title Deed' };

  // Helper functions
  const getName = (obj, field1Ar, field1En, field2Ar, field2En) => {
    if (!obj) return 'N/A';
    if (isArabic) {
      return obj[field1Ar] || obj[field1Ar?.charAt(0).toUpperCase() + field1Ar?.slice(1)] || obj[field1En] || obj[field1En?.charAt(0).toUpperCase() + field1En?.slice(1)] || 'N/A';
    }
    return obj[field1En] || obj[field1En?.charAt(0).toUpperCase() + field1En?.slice(1)] || obj[field1Ar] || obj[field1Ar?.charAt(0).toUpperCase() + field1Ar?.slice(1)] || 'N/A';
  };

  const propertyName = getName(data, 'nameAr', 'nameEng');
  const placeTypeCityName = getName(data.placeTypeCity || data.PlaceTypeCity, 'cityNameAr', 'cityNameEng');

  // Render facilities
  const renderFacilitiesHTML = (facilities) => {
    if (!facilities || facilities.length === 0) return `<div class="no-data">${t.noFacilities}</div>`;
    return `<div class="facilities-grid">${facilities.map(f => {
      const facility = f.facilitiesAndAmenities || f.FacilitiesAndAmenities || f;
      const name = getName(facility, 'nameAr', 'nameEng');
      const quantity = f.quantity || f.Quantity || 1;
      return `<div class="facility-item">${name}${quantity > 1 ? ` <span class="qty">(${quantity})</span>` : ''}</div>`;
    }).join('')}</div>`;
  };

  // Render ownerships
  const renderOwnershipsHTML = (ownerships) => {
    if (!ownerships || ownerships.length === 0) return `<div class="no-data">${t.noOwnership}</div>`;
    return ownerships.map((po, index) => {
      const o = po.propertyOwnership || po.PropertyOwnership || po;
      const isAgent = o.isAgent || o.IsAgent || false;
      const name = getName(o, 'ownershipNameAr', 'ownershipNameEng');
      const nationalityName = getName(o.nationality || o.Nationality, 'nameAr', 'nameEng');
      const identityTypeName = getName(o.identityType || o.IdentityType, 'nameAr', 'nameEng');
      
      return `
        <div class="ownership-card">
          <div class="ownership-header">${isAgent ? t.agent : t.owner}${ownerships.length > 1 ? ` #${index + 1}` : ''}</div>
          <div class="info-grid">
            <div class="info-row"><span class="info-label">${isArabic ? t.nameAr : t.nameEng}:</span><span class="info-value">${name}</span></div>
            ${o.dob || o.DOB ? `<div class="info-row"><span class="info-label">${t.dateOfBirth}:</span><span class="info-value">${formatDate(o.dob || o.DOB)}</span></div>` : ''}
            ${o.nationality || o.Nationality ? `<div class="info-row"><span class="info-label">${t.nationality}:</span><span class="info-value">${nationalityName}</span></div>` : ''}
            ${o.identityType || o.IdentityType ? `<div class="info-row"><span class="info-label">${t.identityType}:</span><span class="info-value">${identityTypeName}</span></div>` : ''}
            ${o.identityNumber || o.IdentityNumber ? `<div class="info-row"><span class="info-label">${t.identityNumber}:</span><span class="info-value highlight">${o.identityNumber || o.IdentityNumber}</span></div>` : ''}
            ${o.mobileNumber || o.MobileNumber ? `<div class="info-row"><span class="info-label">${t.mobile}:</span><span class="info-value">${(o.mobileNumberCountryCode || o.MobileNumberCountryCode || '')} ${o.mobileNumber || o.MobileNumber}</span></div>` : ''}
            ${o.emailId || o.EmailId ? `<div class="info-row"><span class="info-label">${t.email}:</span><span class="info-value">${o.emailId || o.EmailId}</span></div>` : ''}
            ${isAgent && (o.agentNumber || o.AgentNumber) ? `<div class="info-row"><span class="info-label">${t.agentNumber}:</span><span class="info-value highlight">${o.agentNumber || o.AgentNumber}</span></div>` : ''}
          </div>
        </div>
      `;
    }).join('');
  };

  // Render units
  const renderUnitsHTML = (units) => {
    if (!units || units.length === 0) return `<div class="no-data">${t.noUnits}</div>`;
    return units.map((pu, index) => {
      const u = pu.propertyUnit || pu.PropertyUnit || pu;
      const facilities = u.propertyUnitFacilitiesAndAmenities || u.PropertyUnitFacilitiesAndAmenities || [];
      const unitTypeName = getName(u.unitType || u.UnitType, 'nameAr', 'nameEng');
      const unitUsageName = getName(u.unitUsage || u.UnitUsage, 'nameAr', 'nameEng');
      
      return `
        <div class="unit-card">
          <div class="unit-header">${t.unit} ${index + 1}: ${u.unitNumber || u.UnitNumber || 'N/A'}</div>
          <div class="info-grid">
            <div class="info-row"><span class="info-label">${t.unitType}:</span><span class="info-value">${unitTypeName}</span></div>
            <div class="info-row"><span class="info-label">${t.unitUsage}:</span><span class="info-value">${unitUsageName}</span></div>
            <div class="info-row"><span class="info-label">${t.floorNumber}:</span><span class="info-value">${u.floorNumber || u.FloorNumber || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">${t.unitArea}:</span><span class="info-value highlight">${u.unitArea || u.UnitArea || 'N/A'} m²</span></div>
            <div class="info-row"><span class="info-label">${t.numberOfRooms}:</span><span class="info-value">${u.numberOfRooms || u.NumberOfRooms || 'N/A'}</span></div>
            ${u.electricityMeter || u.ElectricityMeter ? `<div class="info-row"><span class="info-label">${t.electricityMeter}:</span><span class="info-value mono">${u.electricityMeter || u.ElectricityMeter}</span></div>` : ''}
            ${u.waterMeter || u.WaterMeter ? `<div class="info-row"><span class="info-label">${t.waterMeter}:</span><span class="info-value mono">${u.waterMeter || u.WaterMeter}</span></div>` : ''}
            ${u.gasMeterNo || u.GasMeterNo ? `<div class="info-row"><span class="info-label">${t.gasMeter}:</span><span class="info-value mono">${u.gasMeterNo || u.GasMeterNo}</span></div>` : ''}
            ${u.countOfParking || u.CountOfParking ? `<div class="info-row"><span class="info-label">${t.parkingSpaces}:</span><span class="info-value">${u.countOfParking || u.CountOfParking}</span></div>` : ''}
          </div>
          ${facilities.length > 0 ? `<div class="unit-facilities"><div class="facilities-title">${t.unitFacilities}:</div>${renderFacilitiesHTML(facilities)}</div>` : ''}
        </div>
      `;
    }).join('');
  };

  // Create page with header and footer
  const createPage = (content, pageNum, totalPages) => `
    <div class="page">
      <div class="page-border">
        <div class="header">
          <div class="header-content">
            <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="logo-fallback">LOGO</div>
            <div class="org-info">
              <div class="org-name-en">${orgNameEng}</div>
              <div class="org-name-ar">${orgNameAr}</div>
            </div>
          </div>
          <div class="header-center">
            <div class="doc-title">${t.propertyDetails}</div>
            <div class="doc-subtitle">Property Details</div>
          </div>
          <div class="header-meta">
            <div class="meta-row"><span class="meta-label">${t.propertyCode}:</span><span class="meta-val">${propertyCode}</span></div>
            <div class="meta-row"><span class="meta-label">${t.page}:</span><span class="meta-val">${pageNum} / ${totalPages}</span></div>
          </div>
        </div>
        <div class="content">
          ${content}
        </div>
        <div class="footer">
          <div class="footer-line"></div>
          <div class="footer-text">${t.printDate}: ${printDate} | ${orgNameAr} - ${t.systemName}</div>
        </div>
      </div>
    </div>
  `;

  // Build page 1 content - Property Information & Ownership
  const page1Content = `
    <div class="section">
      <div class="section-title">${t.propertyInfo}</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">${t.propertyId}:</span><span class="info-value">${data.id || data.Id || 'N/A'}</span></div>
        <div class="info-row"><span class="info-label">${t.propertyCode}:</span><span class="info-value highlight mono">${propertyCode}</span></div>
        <div class="info-row"><span class="info-label">${t.nameEng}:</span><span class="info-value">${propertyName}</span></div>
        <div class="info-row"><span class="info-label">${t.ownerDocType}:</span><span class="info-value">${ownerDocumentTypeMap[data.ownerDocumentType || data.OwnerDocumentType] || 'N/A'}</span></div>
        ${data.titleDeed || data.TitleDeed ? `<div class="info-row"><span class="info-label">${t.titleDeed}:</span><span class="info-value highlight">${data.titleDeed || data.TitleDeed}</span></div>` : ''}
        ${(data.ownerDocumentType || data.OwnerDocumentType) == 1 && (data.identityNumber || data.IdentityNumber) ? `<div class="info-row"><span class="info-label">${t.identityNumber}:</span><span class="info-value mono">${data.identityNumber || data.IdentityNumber}</span></div>` : ''}
        ${(data.ownerDocumentType || data.OwnerDocumentType) == 2 && (data.crNumber || data.CrNumber) ? `<div class="info-row"><span class="info-label">${t.crNumber}:</span><span class="info-value mono">${data.crNumber || data.CrNumber}</span></div>` : ''}
        ${data.issueDate || data.IssueDate ? `<div class="info-row"><span class="info-label">${t.issueDate}:</span><span class="info-value">${formatDate(data.issueDate || data.IssueDate)}</span></div>` : ''}
        ${data.placeTypeCity || data.PlaceTypeCity ? `<div class="info-row"><span class="info-label">${t.placeTypeCity}:</span><span class="info-value">${placeTypeCityName}</span></div>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">${t.ownershipInfo}</div>
      ${renderOwnershipsHTML(data.propertyOwnerships || data.PropertyOwnerships || [])}
    </div>
  `;

  // Build page 2 content - Property Details & Location
  const cityName = getName(data.city || data.City, 'cityNameAr', 'cityNameEng');
  const areaName = getName(data.area || data.Area, 'areaNameAr', 'areaNameEng');
  const districtName = getName(data.district || data.District, 'districtNameAr', 'districtNameEng');
  const realEstateTypeName = getName(data.realEstateType || data.RealEstateType, 'nameAr', 'nameEng');
  const realEstateUsageName = getName(data.realEstateUsage || data.RealEstateUsage, 'nameAr', 'nameEng');

  const page2Content = `
    <div class="section">
      <div class="section-title">${t.propertyDetailsSection}</div>
      <div class="info-grid">
        ${data.municipalLicenseNumber || data.MunicipalLicenseNumber ? `<div class="info-row"><span class="info-label">${t.municipalLicenseNumber}:</span><span class="info-value">${data.municipalLicenseNumber || data.MunicipalLicenseNumber}</span></div>` : ''}
        ${data.realEstateType || data.RealEstateType ? `<div class="info-row"><span class="info-label">${t.realEstateType}:</span><span class="info-value">${realEstateTypeName}</span></div>` : ''}
        ${data.realEstateStatus || data.RealEstateStatus ? `<div class="info-row"><span class="info-label">${t.realEstateStatus}:</span><span class="info-value">${data.realEstateStatus || data.RealEstateStatus}</span></div>` : ''}
        ${data.realEstateUsage || data.RealEstateUsage ? `<div class="info-row"><span class="info-label">${t.realEstateUsage}:</span><span class="info-value">${realEstateUsageName}</span></div>` : ''}
        ${data.realEstateNumber || data.RealEstateNumber ? `<div class="info-row"><span class="info-label">${t.realEstateNumber}:</span><span class="info-value">${data.realEstateNumber || data.RealEstateNumber}</span></div>` : ''}
        ${data.realEstateAge || data.RealEstateAge ? `<div class="info-row"><span class="info-label">${t.realEstateAge}:</span><span class="info-value">${data.realEstateAge || data.RealEstateAge} ${t.years}</span></div>` : ''}
        ${data.unitTotalNumber || data.UnitTotalNumber ? `<div class="info-row"><span class="info-label">${t.totalUnits}:</span><span class="info-value highlight">${data.unitTotalNumber || data.UnitTotalNumber}</span></div>` : ''}
        ${data.floorsTotalNumber || data.FloorsTotalNumber ? `<div class="info-row"><span class="info-label">${t.totalFloors}:</span><span class="info-value">${data.floorsTotalNumber || data.FloorsTotalNumber}</span></div>` : ''}
        ${data.city || data.City ? `<div class="info-row"><span class="info-label">${t.city}:</span><span class="info-value">${cityName}</span></div>` : ''}
        ${data.area || data.Area ? `<div class="info-row"><span class="info-label">${t.area}:</span><span class="info-value">${areaName}</span></div>` : ''}
        ${data.district || data.District ? `<div class="info-row"><span class="info-label">${t.district}:</span><span class="info-value">${districtName}</span></div>` : ''}
        ${data.streetName || data.StreetName ? `<div class="info-row"><span class="info-label">${t.streetName}:</span><span class="info-value">${data.streetName || data.StreetName}</span></div>` : ''}
        ${data.buildingNumber || data.BuildingNumber ? `<div class="info-row"><span class="info-label">${t.buildingNumber}:</span><span class="info-value">${data.buildingNumber || data.BuildingNumber}</span></div>` : ''}
        ${data.postalCode || data.PostalCode ? `<div class="info-row"><span class="info-label">${t.postalCode}:</span><span class="info-value">${data.postalCode || data.PostalCode}</span></div>` : ''}
        <div class="info-row"><span class="info-label">${t.isPartOfCompound}:</span><span class="info-value">${data.isPartOfCompound || data.IsPartOfCompound ? t.yes : t.no}</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">${t.propertyFacilities}</div>
      ${renderFacilitiesHTML(data.propertyFacilitiesAndAmenities || data.PropertyFacilitiesAndAmenities || [])}
    </div>
  `;

  // Build page 3 content - Units
  const page3Content = `
    <div class="section">
      <div class="section-title">${t.unitsDetails}</div>
      ${renderUnitsHTML(data.propertyUnits || data.PropertyUnits || [])}
    </div>
  `;

  const printContent = document.createElement('div');
  printContent.style.position = 'absolute';
  printContent.style.left = '-9999px';
  printContent.style.top = '0';

  printContent.innerHTML = `
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .print-container {
        font-family: 'Segoe UI', 'Arial', 'Tahoma', sans-serif;
        direction: ${isArabic ? 'rtl' : 'ltr'};
        background: white;
      }

      .page {
        width: 210mm;
        min-height: 297mm;
        padding: 8mm;
        background: white;
        page-break-after: always;
      }

      .page:last-child { page-break-after: auto; }

      .page-border {
        border: 2px solid #bf4f1f;
        min-height: calc(297mm - 16mm);
        display: flex;
        flex-direction: column;
      }

      /* Header */
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 2px solid #bf4f1f;
        background: linear-gradient(to bottom, #fef9f7, #ffffff);
      }

      .header-content {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo {
        height: 55px;
        width: auto;
        max-width: 120px;
        object-fit: contain;
      }

      .logo-fallback {
        width: 55px;
        height: 55px;
        background-color: #bf4f1f;
        border-radius: 50%;
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        color: white;
      }

      .org-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .org-name-en {
        font-size: 14px;
        font-weight: 700;
        color: #bf4f1f;
        letter-spacing: 0.5px;
      }

      .org-name-ar {
        font-size: 12px;
        font-weight: 700;
        color: #bf4f1f;
      }

      .header-center {
        text-align: center;
      }

      .doc-title {
        font-size: 20px;
        font-weight: bold;
        color: #bf4f1f;
      }

      .doc-subtitle {
        font-size: 10px;
        color: #888;
        margin-top: 2px;
      }

      .header-meta {
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-size: 9px;
        text-align: ${isArabic ? 'right' : 'left'};
      }

      .header-meta .meta-row {
        display: flex;
        gap: 5px;
        align-items: center;
      }

      .header-meta .meta-label {
        color: #888;
      }

      .header-meta .meta-val {
        font-weight: 600;
        color: #333;
      }
      
      /* Meta Grid for property info summary */
      .meta-grid {
        display: flex;
        justify-content: space-between;
        background: #fdf6f3;
        padding: 12px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        margin-bottom: 15px;
      }

      .meta-item {
        text-align: center;
        flex: 1;
        border-left: 1px solid #e8d5ce;
        padding: 0 8px;
      }

      .meta-item:first-child {
        border-left: none;
      }

      .meta-item .meta-label {
        font-size: 9px;
        color: #888;
        margin-bottom: 4px;
        display: block;
      }

      .meta-item .meta-value {
        font-size: 11px;
        font-weight: bold;
        color: #bf4f1f;
        display: block;
      }

      /* Content */
      .content {
        flex: 1;
        padding: 15px 20px;
      }

      /* Footer */
      .footer {
        padding: 8px 20px 12px;
      }

      .footer-line {
        height: 1px;
        background: #ddd;
        margin-bottom: 8px;
      }

      .footer-text {
        text-align: center;
        font-size: 9px;
        color: #999;
      }

      /* Sections */
      .section {
        margin-bottom: 18px;
      }

      .section-title {
        font-size: 12px;
        font-weight: bold;
        color: #fff;
        padding: 8px 15px;
        background: #bf4f1f;
        margin-bottom: 10px;
      }

      /* Info Grid - Replaces data tables */
      .info-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px 15px;
        padding: 0 5px;
      }

      .info-row {
        display: flex;
        justify-content: space-between;
        padding: 6px 8px;
        border-bottom: 1px solid #f0e0d9;
        font-size: 10px;
        background: #fdf6f3;
      }

      .info-label {
        color: #666;
        font-weight: 600;
      }

      .info-value {
        font-weight: 600;
        color: #333;
      }

      .info-value.highlight {
        color: #bf4f1f;
        font-weight: bold;
      }

      .info-value.mono {
        font-family: monospace;
      }

      /* Cards */
      .ownership-card, .unit-card {
        background: #fdf6f3;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        margin-bottom: 12px;
        overflow: hidden;
      }

      .ownership-header, .unit-header {
        background: #bf4f1f;
        color: white;
        padding: 8px 12px;
        font-weight: bold;
        font-size: 11px;
      }

      .ownership-card .info-grid,
      .unit-card .info-grid {
        padding: 8px 12px;
      }

      /* Unit Facilities */
      .unit-facilities {
        padding: 10px 12px;
        border-top: 1px solid #f0e0d9;
        background: #fff;
      }

      .facilities-title {
        font-weight: 600;
        color: #666;
        font-size: 10px;
        margin-bottom: 6px;
      }

      /* Facilities Grid */
      .facilities-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .facility-item {
        background: #fff;
        border: 1px solid #f0e0d9;
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 10px;
        color: #333;
      }

      .facility-item .qty {
        color: #bf4f1f;
        font-weight: 600;
      }

      /* No Data */
      .no-data {
        color: #999;
        font-style: italic;
        font-size: 10px;
        padding: 10px;
        text-align: center;
        background: #fdf6f3;
        border: 1px dashed #f0e0d9;
        border-radius: 4px;
      }
    </style>

    <div class="print-container">
      ${createPage(page1Content, 1, 3)}
      ${createPage(page2Content, 2, 3)}
      ${createPage(page3Content, 3, 3)}
    </div>
  `;

  document.body.appendChild(printContent);

  try {
    await new Promise(resolve => setTimeout(resolve, 300));

    const pages = printContent.querySelectorAll('.page');
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123
      });

      const imgData = canvas.toDataURL('image/png');
      
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    }

    const pdfBlob = pdf.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } else {
      pdf.save(`Property_${propertyCode}.pdf`);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    document.body.removeChild(printContent);
  }
};

export const generateContractPDF = async (contract, contractDetails = null, organization = null) => {
  const data = contractDetails || contract;

  // Get organization details if not provided
  if (!organization) {
    organization = await getOrganizationForPrint();
  }

  // Helper to get organization field with fallback
  const getOrgField = (field, fallback = '') => {
    if (!organization) return fallback;
    return organization[field] || organization[field.charAt(0).toUpperCase() + field.slice(1)] || fallback;
  };

  // Get logo URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
  const baseUrl = API_BASE_URL.replace('/api', '');
  const logoPath = getOrgField('logo');
  const logoUrl = logoPath ? `${baseUrl}${logoPath}` : '/logo.png';
  const orgNameAr = getOrgField('organizationNameAr', 'عقار لايف');
  const orgNameEng = getOrgField('organizationNameEng', 'Agar Live');

  // Prepare contract-related data
  const contractId = data.contractNumber || data.ContractNumber || data.id || data.Id || 'N/A';
  const createdAt = data.createdAt || data.CreatedAt || data.dateFrom || data.DateFrom;
  const createdAtStr = formatDate(createdAt);
  const statusText = (data.isActive !== undefined ? data.isActive : data.IsActive) ? 'نشط' : 'غير نشط';
  const statusClass = (data.isActive !== undefined ? data.isActive : data.IsActive) ? 'active' : 'inactive';

  // Parties
  const tenants = data.contractTenants || data.ContractTenants || [];
  const firstTenant = tenants[0] || {};
  const tenantNameAr = firstTenant.nameAr || firstTenant.NameAr || firstTenant.tradeNameAr || firstTenant.TradeNameAr || '-';
  const tenantNameEng = firstTenant.nameEng || firstTenant.NameEng || firstTenant.tradeNameEng || firstTenant.TradeNameEng || '-';
  const tenantIdNumber = firstTenant.identityNumber || firstTenant.IdentityNumber || firstTenant.crNumber || firstTenant.CRNumber || '-';
  const tenantMobile = firstTenant.mobileNumber || firstTenant.MobileNumber || '-';

  // Property and units
  const property = data.property || data.Property || {};
  const propertyNameAr = property.nameAr || property.NameAr || '';
  const propertyNameEng = property.nameEng || property.NameEng || '';
  const propertyCode = property.code || property.Code || 'N/A';

  const contractUnits = data.contractUnits || data.ContractUnits || [];
  const units = contractUnits.map(cu => cu.propertyUnit || cu.PropertyUnit).filter(Boolean);

  const totalArea = units.reduce((sum, u) => {
    const area = parseFloat(u.unitArea || u.UnitArea);
    return sum + (isNaN(area) ? 0 : area);
  }, 0);

  // Dates & duration
  const dateFrom = data.dateFrom || data.DateFrom;
  const dateTo = data.dateTo || data.DateTo;
  const dateFromStr = formatDate(dateFrom);
  const dateToStr = formatDate(dateTo);

  let durationMonths = 0;
  try {
    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    if (!isNaN(start) && !isNaN(end) && end >= start) {
      durationMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    }
  } catch { durationMonths = 0; }

  // Financial terms - Get the first financial terms object (it's a single object, not an array)
  // Try multiple possible property names (camelCase, PascalCase, and nested access)
  let terms = data.contractFinancialTerms || data.ContractFinancialTerms || null;
  
  // If not found at top level, check if it's nested or if data itself is the contract
  if (!terms) {
    // Check if data has a nested structure
    if (data.contract && (data.contract.contractFinancialTerms || data.contract.ContractFinancialTerms)) {
      terms = data.contract.contractFinancialTerms || data.contract.ContractFinancialTerms;
    }
    // If still not found, check all possible nested paths
    else if (data.Contract && (data.Contract.contractFinancialTerms || data.Contract.ContractFinancialTerms)) {
      terms = data.Contract.contractFinancialTerms || data.Contract.ContractFinancialTerms;
    }
  }
  
  // Default to empty object if still not found
  terms = terms || {};
  
  // Debug: Log to see what we're getting (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('Contract PDF - Financial Terms Debug:', {
      hasTopLevelTerms: !!(data.contractFinancialTerms || data.ContractFinancialTerms),
      topLevelTerms: data.contractFinancialTerms || data.ContractFinancialTerms,
      finalTermsObject: terms,
      termsKeys: Object.keys(terms)
    });
  }
  
  // Basic rental fees - handle both camelCase and PascalCase property names
  const annualRentPerUnit = terms.annualRentPerUnit ?? terms.AnnualRentPerUnit ?? 0;
  const paymentFrequency = terms.paymentFrequency ?? terms.PaymentFrequency ?? '';
  const brokerageCommission = terms.brokerageCommission ?? terms.BrokerageCommission ?? 0;
  const totalServices = terms.totalServices ?? terms.TotalServices ?? 0;
  const totalRent = terms.totalRent ?? terms.TotalRent ?? 0;
  
  // Payment method
  const paymentMethod = terms.paymentMethod ?? terms.PaymentMethod ?? '';
  const paymentChannels = terms.paymentChannels ?? terms.PaymentChannels ?? '';
  const ibanNumber = terms.ibanNumber ?? terms.IBANNumber ?? '-';
  const ibanAccountHolderName = terms.ibanAccountHolderName ?? terms.IBANAccountHolderName ?? '-';
  
  // Security deposit
  const securityDepositEnabled = terms.securityDepositEnabled ?? terms.SecurityDepositEnabled ?? false;
  const securityDepositAmount = terms.securityDepositAmount ?? terms.SecurityDepositAmount ?? 0;
  
  // Daily fine
  const dailyFineEnabled = terms.dailyFineEnabled ?? terms.DailyFineEnabled ?? false;
  const dailyFineAmount = terms.dailyFineAmount ?? terms.DailyFineAmount ?? 0;
  
  // Retainer
  const retainerEnabled = terms.retainerEnabled ?? terms.RetainerEnabled ?? false;
  const retainerAmount = terms.retainerAmount ?? terms.RetainerAmount ?? 0;
  const retainerDeductionMethod = terms.retainerDeductionMethod ?? terms.RetainerDeductionMethod ?? '';
  const jointAccountNumber = terms.jointAccountNumber ?? terms.JointAccountNumber ?? '-';
  
  // Brokerage office fee
  const brokerageOfficeFeeEnabled = terms.brokerageOfficeFeeEnabled ?? terms.BrokerageOfficeFeeEnabled ?? false;
  const brokerageOfficeFeeAmount = terms.brokerageOfficeFeeAmount ?? terms.BrokerageOfficeFeeAmount ?? 0;
  const brokerageOfficeFeePaidBy = terms.brokerageOfficeFeePaidBy ?? terms.BrokerageOfficeFeePaidBy ?? '';
  const brokerageOfficeFeeDueDate = terms.brokerageOfficeFeeDueDate ?? terms.BrokerageOfficeFeeDueDate ?? null;
  const brokerageOfficeFeeDueDateStr = formatDate(brokerageOfficeFeeDueDate);
  
  // Contract starting balance
  const totalPaidAmount = terms.totalPaidAmount ?? terms.TotalPaidAmount ?? 0;
  const paidServicesAmount = terms.paidServicesAmount ?? terms.PaidServicesAmount ?? 0;
  const firstBillDueRent = terms.firstBillDueRent ?? terms.FirstBillDueRent ?? 0;
  const firstBillDueServices = terms.firstBillDueServices ?? terms.FirstBillDueServices ?? 0;
  const scheduledAmount = terms.scheduledAmount ?? terms.ScheduledAmount ?? 0;
  const scheduledStartDate = terms.scheduledStartDate ?? terms.ScheduledStartDate ?? null;
  const scheduledStartDateStr = formatDate(scheduledStartDate);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (isNaN(num)) return `${value}`;
    return num.toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const paymentFrequencyLabel = (() => {
    switch (paymentFrequency) {
      case 'Monthly': return 'شهري';
      case 'Quarterly': return 'ربع سنوي';
      case 'SemiAnnual': return 'نصف سنوي';
      case 'Annual': return 'سنوي';
      default: return paymentFrequency || 'غير محدد';
    }
  })();

  const paymentMethodLabel = paymentMethod === 'OnlineOnly' ? 'الدفع الإلكتروني فقط' : 
    paymentMethod === 'OnlineAndBO' ? 'الدفع الإلكتروني أو عبر المكتب' : paymentMethod || '-';
  
  // Parse payment channels (JSON array)
  let paymentChannelsList = [];
  try {
    if (paymentChannels) {
      const parsed = typeof paymentChannels === 'string' ? JSON.parse(paymentChannels) : paymentChannels;
      paymentChannelsList = Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    paymentChannelsList = [];
  }
  
  const retainerDeductionMethodLabel = retainerDeductionMethod === 'JointAccount' ? 'حساب مشترك' : 
    retainerDeductionMethod === 'Other' ? 'أخرى' : retainerDeductionMethod || '-';
  
  const brokerageOfficeFeePaidByLabel = brokerageOfficeFeePaidBy === 'Lessor' ? 'المؤجر' : 
    brokerageOfficeFeePaidBy === 'Tenant' ? 'المستأجر' : brokerageOfficeFeePaidBy || '-';

  const printDate = new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: '2-digit', day: '2-digit' });

  // Units HTML
  const unitsTableRows = units.map((unit, idx) => {
    const unitNum = unit.unitNumber || unit.UnitNumber || `${idx + 1}`;
    const unitArea = unit.unitArea || unit.UnitArea || 0;
    const unitType = unit.unitType || unit.UnitType || {};
    const typeName = unitType.nameAr || unitType.NameAr || unitType.nameEng || unitType.NameEng || '-';
    return `<tr><td>${idx + 1}</td><td>${unitNum}</td><td>${typeName}</td><td>${unitArea} م²</td></tr>`;
  }).join('');

  // Create sections as separate pages
  const createPage = (content, pageNum, totalPages) => `
    <div class="page">
      <div class="page-border">
        <div class="header">
          <div class="header-content">
            <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="logo-fallback">LOGO</div>
            <div class="org-info">
              <div class="org-name-en">${orgNameEng}</div>
              <div class="org-name-ar">${orgNameAr}</div>
            </div>
          </div>
          <div class="header-center">
            <div class="contract-title">عقد إيجار</div>
            <div class="contract-subtitle">Rental Agreement</div>
          </div>
          <div class="header-meta">
            <div class="meta-row"><span class="meta-label">رقم العقد:</span><span class="meta-val">${contractId}</span></div>
            <div class="meta-row"><span class="meta-label">الحالة:</span><span class="status ${statusClass}">${statusText}</span></div>
            <div class="meta-row"><span class="meta-label">صفحة:</span><span class="meta-val">${pageNum} / ${totalPages}</span></div>
          </div>
        </div>
        <div class="content">
          ${content}
        </div>
        <div class="footer">
          <div class="footer-line"></div>
          <div class="footer-text">تاريخ الطباعة: ${printDate} | ${orgNameAr} - نظام إدارة العقارات</div>
        </div>
      </div>
    </div>
  `;

  // Page 1 content
  const page1Content = `
    <div class="section">
      <div class="section-title">بيانات العقد</div>
      <div class="meta-grid">
        <div class="meta-item"><div class="meta-label">رقم العقد</div><div class="meta-value">${contractId}</div></div>
        <div class="meta-item"><div class="meta-label">تاريخ الإنشاء</div><div class="meta-value">${createdAtStr}</div></div>
        <div class="meta-item"><div class="meta-label">المدة</div><div class="meta-value">${durationMonths} شهر</div></div>
        <div class="meta-item"><div class="meta-label">من</div><div class="meta-value">${dateFromStr}</div></div>
        <div class="meta-item"><div class="meta-label">إلى</div><div class="meta-value">${dateToStr}</div></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">بيانات المستأجر</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">الاسم (عربي):</span><span class="info-value">${tenantNameAr}</span></div>
        <div class="info-row"><span class="info-label">الاسم (إنجليزي):</span><span class="info-value">${tenantNameEng}</span></div>
        <div class="info-row"><span class="info-label">رقم الهوية:</span><span class="info-value">${tenantIdNumber}</span></div>
        <div class="info-row"><span class="info-label">رقم الجوال:</span><span class="info-value">${tenantMobile}</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">بيانات العقار</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">اسم العقار:</span><span class="info-value">${propertyNameAr || propertyNameEng || '-'}</span></div>
        <div class="info-row"><span class="info-label">كود العقار:</span><span class="info-value">${propertyCode}</span></div>
        <div class="info-row"><span class="info-label">عدد الوحدات:</span><span class="info-value">${units.length}</span></div>
        <div class="info-row"><span class="info-label">المساحة الإجمالية:</span><span class="info-value">${totalArea.toFixed(2)} م²</span></div>
      </div>
    </div>

    ${units.length > 0 ? `
    <div class="section">
      <div class="section-title">الوحدات المؤجرة</div>
      <table class="units-table">
        <thead><tr><th>#</th><th>رقم الوحدة</th><th>النوع</th><th>المساحة</th></tr></thead>
        <tbody>${unitsTableRows}</tbody>
      </table>
    </div>
    ` : ''}
  `;

  // Page 2 content
  const page2Content = `
    <div class="section">
      <div class="section-title">التفاصيل المالية الأساسية</div>
      <div class="financial-box">
        <div class="financial-row"><span class="fin-label">الإيجار السنوي للوحدة</span><span class="fin-value highlight">${formatCurrency(annualRentPerUnit)} ريال</span></div>
        <div class="financial-row"><span class="fin-label">دورية الدفع</span><span class="fin-value">${paymentFrequencyLabel || 'غير محدد'}</span></div>
        ${brokerageCommission > 0 ? `<div class="financial-row"><span class="fin-label">عمولة الوساطة</span><span class="fin-value">${formatCurrency(brokerageCommission)} ريال</span></div>` : ''}
        <div class="financial-row"><span class="fin-label">رسوم الخدمات</span><span class="fin-value">${formatCurrency(totalServices)} ريال</span></div>
        <div class="financial-row total"><span class="fin-label">إجمالي الإيجار السنوي</span><span class="fin-value highlight">${formatCurrency(totalRent)} ريال</span></div>
      </div>
    </div>

    ${securityDepositEnabled ? `
    <div class="section">
      <div class="section-title">مبلغ التأمين</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">مبلغ التأمين:</span><span class="info-value highlight">${formatCurrency(securityDepositAmount)} ريال</span></div>
      </div>
    </div>
    ` : ''}

    ${dailyFineEnabled ? `
    <div class="section">
      <div class="section-title">الغرامة اليومية</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">مبلغ الغرامة اليومية:</span><span class="info-value">${formatCurrency(dailyFineAmount)} ريال</span></div>
      </div>
    </div>
    ` : ''}

    ${retainerEnabled ? `
    <div class="section">
      <div class="section-title">الضمان</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">مبلغ الضمان:</span><span class="info-value">${formatCurrency(retainerAmount)} ريال</span></div>
        <div class="info-row"><span class="info-label">طريقة خصم الضمان:</span><span class="info-value">${retainerDeductionMethodLabel}</span></div>
        ${retainerDeductionMethod === 'JointAccount' && jointAccountNumber ? `<div class="info-row"><span class="info-label">رقم الحساب المشترك:</span><span class="info-value">${jointAccountNumber}</span></div>` : ''}
      </div>
    </div>
    ` : ''}

    ${brokerageOfficeFeeEnabled ? `
    <div class="section">
      <div class="section-title">رسوم مكتب الوساطة</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">مبلغ الرسوم:</span><span class="info-value">${formatCurrency(brokerageOfficeFeeAmount)} ريال</span></div>
        <div class="info-row"><span class="info-label">يدفع من قبل:</span><span class="info-value">${brokerageOfficeFeePaidByLabel}</span></div>
        ${brokerageOfficeFeeDueDateStr !== 'N/A' ? `<div class="info-row"><span class="info-label">تاريخ الاستحقاق:</span><span class="info-value">${brokerageOfficeFeeDueDateStr}</span></div>` : ''}
      </div>
    </div>
    ` : ''}

    <div class="section">
      <div class="section-title">طريقة الدفع</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">طريقة الدفع:</span><span class="info-value">${paymentMethodLabel}</span></div>
        ${paymentChannelsList.length > 0 ? `<div class="info-row"><span class="info-label">قنوات الدفع:</span><span class="info-value">${paymentChannelsList.join('، ')}</span></div>` : ''}
        ${ibanNumber && ibanNumber !== '-' ? `<div class="info-row"><span class="info-label">رقم الآيبان:</span><span class="info-value mono">${ibanNumber}</span></div>` : ''}
        ${ibanAccountHolderName && ibanAccountHolderName !== '-' ? `<div class="info-row"><span class="info-label">اسم صاحب الحساب:</span><span class="info-value">${ibanAccountHolderName}</span></div>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">الرصيد الابتدائي للعقد</div>
      <div class="financial-box">
        ${totalPaidAmount > 0 ? `<div class="financial-row"><span class="fin-label">إجمالي المبلغ المدفوع</span><span class="fin-value">${formatCurrency(totalPaidAmount)} ريال</span></div>` : ''}
        ${paidServicesAmount > 0 ? `<div class="financial-row"><span class="fin-label">المبلغ المدفوع للخدمات</span><span class="fin-value">${formatCurrency(paidServicesAmount)} ريال</span></div>` : ''}
        ${firstBillDueRent > 0 ? `<div class="financial-row"><span class="fin-label">إيجار الفاتورة الأولى المستحق</span><span class="fin-value">${formatCurrency(firstBillDueRent)} ريال</span></div>` : ''}
        ${firstBillDueServices > 0 ? `<div class="financial-row"><span class="fin-label">خدمات الفاتورة الأولى المستحقة</span><span class="fin-value">${formatCurrency(firstBillDueServices)} ريال</span></div>` : ''}
        ${scheduledAmount > 0 ? `<div class="financial-row total"><span class="fin-label">المبلغ المتبقي</span><span class="fin-value due">${formatCurrency(scheduledAmount)} ريال</span></div>` : ''}
        ${scheduledStartDateStr !== 'N/A' ? `<div class="financial-row"><span class="fin-label">تاريخ بداية الجدولة</span><span class="fin-value">${scheduledStartDateStr}</span></div>` : ''}
      </div>
    </div>

    <div class="section">
      <div class="section-title">الشروط والأحكام</div>
      <div class="terms-box">
        <div class="term-item"><span class="term-num">1.</span><span>يلتزم المستأجر بدفع الإيجار في المواعيد المحددة دون تأخير.</span></div>
        <div class="term-item"><span class="term-num">2.</span><span>يلتزم المستأجر بالمحافظة على العقار واستخدامه للغرض المتفق عليه.</span></div>
        <div class="term-item"><span class="term-num">3.</span><span>لا يجوز للمستأجر إجراء تعديلات على العقار دون موافقة كتابية من المؤجر.</span></div>
        <div class="term-item"><span class="term-num">4.</span><span>عند انتهاء العقد، يسلم المستأجر العقار بحالته الأصلية مع مراعاة الاستهلاك العادي.</span></div>
        <div class="term-item"><span class="term-num">5.</span><span>يحق للمؤجر فسخ العقد في حال مخالفة أي من الشروط أو التأخر في السداد.</span></div>
      </div>
    </div>

    <div class="signatures">
      <div class="sig-box">
        <div class="sig-title">المؤجر</div>
        <div class="sig-line"></div>
        <div class="sig-hint">التوقيع والتاريخ</div>
      </div>
      <div class="sig-box">
        <div class="sig-title">المستأجر</div>
        <div class="sig-line"></div>
        <div class="sig-hint">التوقيع والتاريخ</div>
      </div>
    </div>
  `;

  const printContent = document.createElement('div');
  printContent.style.position = 'absolute';
  printContent.style.left = '-9999px';
  printContent.style.top = '0';

  printContent.innerHTML = `
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .print-container {
        font-family: 'Segoe UI', 'Arial', 'Tahoma', sans-serif;
        direction: rtl;
        background: white;
      }

      .page {
        width: 210mm;
        min-height: 297mm;
        padding: 8mm;
        background: white;
        page-break-after: always;
      }

      .page:last-child { page-break-after: auto; }

      .page-border {
        border: 2px solid #bf4f1f;
        min-height: calc(297mm - 16mm);
        display: flex;
        flex-direction: column;
      }

      /* Header - Same style as Property PDF */
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 2px solid #bf4f1f;
        background: linear-gradient(to bottom, #fef9f7, #ffffff);
      }

      .header-content {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo {
        height: 55px;
        width: auto;
        max-width: 120px;
        object-fit: contain;
      }

      .logo-fallback {
        width: 55px;
        height: 55px;
        background-color: #bf4f1f;
        border-radius: 50%;
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        color: white;
      }

      .org-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .org-name-en {
        font-size: 14px;
        font-weight: 700;
        color: #bf4f1f;
        letter-spacing: 0.5px;
      }

      .org-name-ar {
        font-size: 12px;
        font-weight: 700;
        color: #bf4f1f;
      }

      .header-center {
        text-align: center;
      }

      .contract-title {
        font-size: 20px;
        font-weight: bold;
        color: #bf4f1f;
      }

      .contract-subtitle {
        font-size: 10px;
        color: #888;
        margin-top: 2px;
      }

      .header-meta {
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-size: 9px;
        text-align: left;
      }

      .header-meta .meta-row {
        display: flex;
        gap: 5px;
        align-items: center;
      }

      .header-meta .meta-label {
        color: #888;
      }

      .header-meta .meta-val {
        font-weight: 600;
        color: #333;
      }

      .status {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 8px;
        font-weight: 600;
      }

      .status.active {
        background: #d4edda;
        color: #155724;
      }

      .status.inactive {
        background: #f8d7da;
        color: #721c24;
      }

      .content { flex: 1; padding: 15px 20px; }

      .footer {
        padding: 8px 20px 12px;
      }

      .footer-line {
        height: 1px;
        background: #ddd;
        margin-bottom: 8px;
      }

      .footer-text {
        text-align: center;
        font-size: 9px;
        color: #999;
      }

      .section { margin-bottom: 18px; }

      .section-title {
        font-size: 12px;
        font-weight: bold;
        color: #fff;
        padding: 8px 15px;
        background: #bf4f1f;
        margin-bottom: 10px;
      }

      .meta-grid {
        display: flex;
        justify-content: space-between;
        background: #fdf6f3;
        padding: 12px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
      }

      .meta-item { 
        text-align: center; 
        flex: 1; 
        border-left: 1px solid #e8d5ce;
        padding: 0 8px;
      }
      
      .meta-item:last-child { border-left: none; }
      
      .meta-label { font-size: 9px; color: #888; margin-bottom: 4px; }
      .meta-value { font-size: 11px; font-weight: bold; color: #bf4f1f; }

      .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 15px; padding: 0 5px; }

      .info-row {
        display: flex;
        justify-content: space-between;
        padding: 6px 8px;
        border-bottom: 1px solid #f0e0d9;
        font-size: 10px;
        background: #fdf6f3;
      }

      .info-label { color: #666; }
      .info-value { font-weight: 600; color: #333; }

      .units-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 10px;
      }

      .units-table th {
        background: #bf4f1f;
        color: white;
        padding: 8px;
        text-align: center;
        font-weight: 600;
      }

      .units-table td {
        border: 1px solid #e8d5ce;
        padding: 7px;
        text-align: center;
      }

      .units-table tr:nth-child(even) td { background: #fdf6f3; }

      .financial-box {
        background: #fdf6f3;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        padding: 10px 15px;
      }

      .financial-row {
        display: flex;
        justify-content: space-between;
        padding: 8px 5px;
        border-bottom: 1px solid #f0e0d9;
        font-size: 10px;
      }

      .financial-row:last-child { border-bottom: none; }

      .financial-row.total {
        border-top: 2px solid #bf4f1f;
        margin-top: 8px;
        padding-top: 10px;
        font-weight: bold;
        font-size: 12px;
      }

      .fin-label { color: #555; }
      .fin-value { font-weight: 600; color: #333; }
      .fin-value.highlight { color: #bf4f1f; font-weight: bold; }
      .fin-value.due { color: #dc3545; font-weight: bold; }

      .terms-box {
        background: #fdf6f3;
        padding: 10px 15px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        font-size: 10px;
      }

      .term-item {
        display: flex;
        gap: 8px;
        margin-bottom: 6px;
        line-height: 1.6;
      }

      .term-num { font-weight: bold; color: #bf4f1f; min-width: 18px; }

      .signatures {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 30px;
        margin-top: 20px;
        padding-top: 15px;
      }

      .sig-box { 
        text-align: center; 
        padding: 15px;
        border: 1px dashed #bf4f1f;
        border-radius: 4px;
        background: #fdf6f3;
      }
      
      .sig-title { font-weight: bold; color: #bf4f1f; font-size: 12px; margin-bottom: 35px; }
      .sig-line { border-top: 1px solid #bf4f1f; margin: 0 25px; }
      .sig-hint { font-size: 8px; color: #888; margin-top: 5px; }
    </style>

    <div class="print-container">
      ${createPage(page1Content, 1, 2)}
      ${createPage(page2Content, 2, 2)}
    </div>
  `;

  document.body.appendChild(printContent);

  try {
    await new Promise(resolve => setTimeout(resolve, 300));

    const pages = printContent.querySelectorAll('.page');
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794, // A4 at 96 DPI
        height: 1123
      });

      const imgData = canvas.toDataURL('image/png');
      
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    }

    const pdfBlob = pdf.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } else {
      pdf.save(`Contract_${contractId}.pdf`);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    document.body.removeChild(printContent);
  }
};

export const generateBrokerageAgreementPDF = async (agreement, agreementDetails = null, organization = null) => {
  const data = agreementDetails || agreement;

  // Get organization details if not provided
  if (!organization) {
    organization = await getOrganizationForPrint();
  }

  // Helper to get organization field with fallback
  const getOrgField = (field, fallback = '') => {
    if (!organization) return fallback;
    return organization[field] || organization[field.charAt(0).toUpperCase() + field.slice(1)] || fallback;
  };

  // Get logo URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
  const baseUrl = API_BASE_URL.replace('/api', '');
  const logoPath = getOrgField('logo');
  const logoUrl = logoPath ? `${baseUrl}${logoPath}` : '/logo.png';
  const orgNameAr = getOrgField('organizationNameAr', 'عقار لايف');
  const orgNameEng = getOrgField('organizationNameEng', 'Agar Live');

  // Helper function to get name with fallback
  const getName = (obj, field1Ar, field1En) => {
    if (!obj) return 'N/A';
    return obj[field1Ar] || obj[field1Ar?.charAt(0).toUpperCase() + field1Ar?.slice(1)] || 
           obj[field1En] || obj[field1En?.charAt(0).toUpperCase() + field1En?.slice(1)] || 'N/A';
  };

  // Prepare agreement-related data
  const agreementId = data.id || data.Id || 'N/A';
  const dateFrom = data.dateFrom || data.DateFrom;
  const dateTo = data.dateTo || data.DateTo;
  const dateFromStr = formatDate(dateFrom);
  const dateToStr = formatDate(dateTo);
  const createdAt = data.createdAt || data.CreatedAt;
  const createdAtStr = formatDate(createdAt);
  const statusText = (data.isActive !== undefined ? data.isActive : data.IsActive) ? 'نشط' : 'غير نشط';
  const statusClass = (data.isActive !== undefined ? data.isActive : data.IsActive) ? 'active' : 'inactive';

  // Calculate duration
  let durationMonths = 0;
  try {
    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    if (!isNaN(start) && !isNaN(end) && end >= start) {
      durationMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    }
  } catch { durationMonths = 0; }

  // Property
  const property = data.property || data.Property || {};
  const propertyNameAr = property.nameAr || property.NameAr || '';
  const propertyNameEng = property.nameEng || property.NameEng || '';
  const propertyCode = property.code || property.Code || 'N/A';

  // Lessor (Property Owner)
  const lessor = data.brokerageAgreementLessor || data.BrokerageAgreementLessor || {};
  const propertyOwnership = lessor.propertyOwnership || lessor.PropertyOwnership || {};
  const lessorNameAr = getName(propertyOwnership, 'ownershipNameAr', 'ownershipNameEng');
  const lessorNameEng = propertyOwnership.ownershipNameEng || propertyOwnership.OwnershipNameEng || propertyOwnership.ownershipNameAr || propertyOwnership.OwnershipNameAr || '-';
  const lessorIdNumber = propertyOwnership.identityNumber || propertyOwnership.IdentityNumber || propertyOwnership.crNumber || propertyOwnership.CrNumber || '-';
  const lessorMobile = propertyOwnership.mobileNumber || propertyOwnership.MobileNumber || '-';

  // Brokerage Organization
  const brokerageOrg = data.brokerageAgreementOrganization || data.BrokerageAgreementOrganization || {};
  const org = brokerageOrg.organization || brokerageOrg.Organization || {};
  const brokerageOrgNameAr = getName(org, 'organizationNameAr', 'organizationNameEng');
  const brokerageOrgNameEng = org.organizationNameEng || org.OrganizationNameEng || org.organizationNameAr || org.OrganizationNameAr || '-';
  const brokerageOrgCR = org.crNumber || org.CRNumber || '-';

  // Units
  const agreementUnits = data.brokerageAgreementUnits || data.BrokerageAgreementUnits || [];
  const units = agreementUnits.map(au => au.propertyUnit || au.PropertyUnit).filter(Boolean);

  const totalArea = units.reduce((sum, u) => {
    const area = parseFloat(u.unitArea || u.UnitArea);
    return sum + (isNaN(area) ? 0 : area);
  }, 0);

  // Scopes
  const scopes = data.brokerageAgreementScopes || data.BrokerageAgreementScopes || [];
  const scopeTypes = scopes.map(s => s.scopeType || s.ScopeType).filter(Boolean);
  const scopeLabels = scopeTypes.map(st => {
    switch(st) {
      case 'RentCollection': return 'جمع الإيجارات';
      case 'UnitsLeasing': return 'تأجير الوحدات';
      case 'UnitsMarketing': return 'تسويق الوحدات';
      default: return st;
    }
  });

  // Terms
  const terms = data.brokerageAgreementTerms || data.BrokerageAgreementTerms || {};
  const defaultTermsAr = terms.defaultTermsAr || terms.DefaultTermsAr || '';
  const defaultTermsEng = terms.defaultTermsEng || terms.DefaultTermsEng || '';
  const additionalTermsAr = terms.additionalTermsAr || terms.AdditionalTermsAr || '';
  const additionalTermsEng = terms.additionalTermsEng || terms.AdditionalTermsEng || '';

  const printDate = new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: '2-digit', day: '2-digit' });

  // Units HTML
  const unitsTableRows = units.map((unit, idx) => {
    const unitNum = unit.unitNumber || unit.UnitNumber || `${idx + 1}`;
    const unitArea = unit.unitArea || unit.UnitArea || 0;
    const unitType = unit.unitType || unit.UnitType || {};
    const typeName = getName(unitType, 'nameAr', 'nameEng');
    const agreementUnit = agreementUnits[idx] || {};
    const agreementStatus = agreementUnit.agreementStatus !== false && agreementUnit.agreementStatus !== 'false';
    const statusText = agreementStatus ? 'نشط' : 'غير نشط';
    
    return `<tr><td>${idx + 1}</td><td>${unitNum}</td><td>${typeName}</td><td>${unitArea} م²</td><td>${statusText}</td></tr>`;
  }).join('');

  // Create sections as separate pages
  const createPage = (content, pageNum, totalPages) => `
    <div class="page">
      <div class="page-border">
        <div class="header">
          <div class="header-content">
            <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="logo-fallback">LOGO</div>
            <div class="org-info">
              <div class="org-name-en">${orgNameEng}</div>
              <div class="org-name-ar">${orgNameAr}</div>
            </div>
          </div>
          <div class="header-center">
            <div class="contract-title">اتفاقية وساطة</div>
            <div class="contract-subtitle">Brokerage Agreement</div>
          </div>
          <div class="header-meta">
            <div class="meta-row"><span class="meta-label">رقم الاتفاقية:</span><span class="meta-val">${agreementId}</span></div>
            <div class="meta-row"><span class="meta-label">الحالة:</span><span class="status ${statusClass}">${statusText}</span></div>
            <div class="meta-row"><span class="meta-label">صفحة:</span><span class="meta-val">${pageNum} / ${totalPages}</span></div>
          </div>
        </div>
        <div class="content">
          ${content}
        </div>
        <div class="footer">
          <div class="footer-line"></div>
          <div class="footer-text">تاريخ الطباعة: ${printDate} | ${orgNameAr} - نظام إدارة العقارات</div>
        </div>
      </div>
    </div>
  `;

  // Page 1 content - Agreement Information
  const page1Content = `
    <div class="section">
      <div class="section-title">بيانات الاتفاقية</div>
      <div class="meta-grid">
        <div class="meta-item"><div class="meta-label">رقم الاتفاقية</div><div class="meta-value">${agreementId}</div></div>
        <div class="meta-item"><div class="meta-label">تاريخ الإنشاء</div><div class="meta-value">${createdAtStr}</div></div>
        <div class="meta-item"><div class="meta-label">المدة</div><div class="meta-value">${durationMonths} شهر</div></div>
        <div class="meta-item"><div class="meta-label">من</div><div class="meta-value">${dateFromStr}</div></div>
        <div class="meta-item"><div class="meta-label">إلى</div><div class="meta-value">${dateToStr}</div></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">بيانات العقار</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">اسم العقار:</span><span class="info-value">${propertyNameAr || propertyNameEng || '-'}</span></div>
        <div class="info-row"><span class="info-label">كود العقار:</span><span class="info-value">${propertyCode}</span></div>
        <div class="info-row"><span class="info-label">عدد الوحدات:</span><span class="info-value">${units.length}</span></div>
        <div class="info-row"><span class="info-label">المساحة الإجمالية:</span><span class="info-value">${totalArea.toFixed(2)} م²</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">بيانات المؤجر (مالك العقار)</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">الاسم (عربي):</span><span class="info-value">${lessorNameAr}</span></div>
        <div class="info-row"><span class="info-label">الاسم (إنجليزي):</span><span class="info-value">${lessorNameEng}</span></div>
        <div class="info-row"><span class="info-label">رقم الهوية/السجل:</span><span class="info-value">${lessorIdNumber}</span></div>
        <div class="info-row"><span class="info-label">رقم الجوال:</span><span class="info-value">${lessorMobile}</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">بيانات منظمة الوساطة</div>
      <div class="info-grid">
        <div class="info-row"><span class="info-label">اسم المنظمة (عربي):</span><span class="info-value">${brokerageOrgNameAr}</span></div>
        <div class="info-row"><span class="info-label">اسم المنظمة (إنجليزي):</span><span class="info-value">${brokerageOrgNameEng}</span></div>
        <div class="info-row"><span class="info-label">رقم السجل التجاري:</span><span class="info-value">${brokerageOrgCR}</span></div>
      </div>
    </div>

    ${units.length > 0 ? `
    <div class="section">
      <div class="section-title">الوحدات المشمولة بالاتفاقية</div>
      <table class="units-table">
        <thead><tr><th>#</th><th>رقم الوحدة</th><th>النوع</th><th>المساحة</th><th>الحالة</th></tr></thead>
        <tbody>${unitsTableRows}</tbody>
      </table>
    </div>
    ` : ''}

    ${scopeTypes.length > 0 ? `
    <div class="section">
      <div class="section-title">نطاق الاتفاقية</div>
      <div class="scope-box">
        ${scopeLabels.map(label => `<div class="scope-item">• ${label}</div>`).join('')}
      </div>
    </div>
    ` : ''}
  `;

  // Page 2 content - Terms
  const page2Content = `
    ${defaultTermsAr ? `
    <div class="section">
      <div class="section-title">الشروط الأساسية</div>
      <div class="terms-box">
        <div class="terms-content">${defaultTermsAr.replace(/\n/g, '<br>')}</div>
      </div>
    </div>
    ` : ''}

    ${additionalTermsAr ? `
    <div class="section">
      <div class="section-title">الشروط الإضافية</div>
      <div class="terms-box">
        <div class="terms-content">${additionalTermsAr.replace(/\n/g, '<br>')}</div>
      </div>
    </div>
    ` : ''}

    <div class="signatures">
      <div class="sig-box">
        <div class="sig-title">المؤجر (مالك العقار)</div>
        <div class="sig-line"></div>
        <div class="sig-hint">التوقيع والتاريخ</div>
      </div>
      <div class="sig-box">
        <div class="sig-title">منظمة الوساطة</div>
        <div class="sig-line"></div>
        <div class="sig-hint">التوقيع والتاريخ</div>
      </div>
    </div>
  `;

  const printContent = document.createElement('div');
  printContent.style.position = 'absolute';
  printContent.style.left = '-9999px';
  printContent.style.top = '0';

  printContent.innerHTML = `
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .print-container {
        font-family: 'Segoe UI', 'Arial', 'Tahoma', sans-serif;
        direction: rtl;
        background: white;
      }

      .page {
        width: 210mm;
        min-height: 297mm;
        padding: 8mm;
        background: white;
        page-break-after: always;
      }

      .page:last-child { page-break-after: auto; }

      .page-border {
        border: 2px solid #bf4f1f;
        min-height: calc(297mm - 16mm);
        display: flex;
        flex-direction: column;
      }

      /* Header - Same style as Contract PDF */
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 2px solid #bf4f1f;
        background: linear-gradient(to bottom, #fef9f7, #ffffff);
      }

      .header-content {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo {
        height: 55px;
        width: auto;
        max-width: 120px;
        object-fit: contain;
      }

      .logo-fallback {
        width: 55px;
        height: 55px;
        background-color: #bf4f1f;
        border-radius: 50%;
        display: none;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
        color: white;
      }

      .org-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .org-name-en {
        font-size: 14px;
        font-weight: 700;
        color: #bf4f1f;
        letter-spacing: 0.5px;
      }

      .org-name-ar {
        font-size: 12px;
        font-weight: 700;
        color: #bf4f1f;
      }

      .header-center {
        text-align: center;
      }

      .contract-title {
        font-size: 20px;
        font-weight: bold;
        color: #bf4f1f;
      }

      .contract-subtitle {
        font-size: 10px;
        color: #888;
        margin-top: 2px;
      }

      .header-meta {
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-size: 9px;
        text-align: left;
      }

      .header-meta .meta-row {
        display: flex;
        gap: 5px;
        align-items: center;
      }

      .header-meta .meta-label {
        color: #888;
      }

      .header-meta .meta-val {
        font-weight: 600;
        color: #333;
      }

      .status {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 8px;
        font-weight: 600;
      }

      .status.active {
        background: #d4edda;
        color: #155724;
      }

      .status.inactive {
        background: #f8d7da;
        color: #721c24;
      }

      .content { flex: 1; padding: 15px 20px; }

      .footer {
        padding: 8px 20px 12px;
      }

      .footer-line {
        height: 1px;
        background: #ddd;
        margin-bottom: 8px;
      }

      .footer-text {
        text-align: center;
        font-size: 9px;
        color: #999;
      }

      .section { margin-bottom: 18px; }

      .section-title {
        font-size: 12px;
        font-weight: bold;
        color: #fff;
        padding: 8px 15px;
        background: #bf4f1f;
        margin-bottom: 10px;
      }

      .meta-grid {
        display: flex;
        justify-content: space-between;
        background: #fdf6f3;
        padding: 12px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
      }

      .meta-item { 
        text-align: center; 
        flex: 1; 
        border-left: 1px solid #e8d5ce;
        padding: 0 8px;
      }
      
      .meta-item:first-child { border-left: none; }
      
      .meta-label { font-size: 9px; color: #888; margin-bottom: 4px; display: block; }
      .meta-value { font-size: 11px; font-weight: bold; color: #bf4f1f; display: block; }

      .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 15px; padding: 0 5px; }

      .info-row {
        display: flex;
        justify-content: space-between;
        padding: 6px 8px;
        border-bottom: 1px solid #f0e0d9;
        font-size: 10px;
        background: #fdf6f3;
      }

      .info-label { color: #666; }
      .info-value { font-weight: 600; color: #333; }

      .units-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 10px;
      }

      .units-table th {
        background: #bf4f1f;
        color: white;
        padding: 8px;
        text-align: center;
        font-weight: 600;
      }

      .units-table td {
        border: 1px solid #e8d5ce;
        padding: 7px;
        text-align: center;
      }

      .units-table tr:nth-child(even) td { background: #fdf6f3; }

      .scope-box {
        background: #fdf6f3;
        padding: 10px 15px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        font-size: 10px;
      }

      .scope-item {
        margin-bottom: 6px;
        line-height: 1.6;
      }

      .terms-box {
        background: #fdf6f3;
        padding: 10px 15px;
        border: 1px solid #f0e0d9;
        border-radius: 4px;
        font-size: 10px;
        line-height: 1.8;
      }

      .terms-content {
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      .signatures {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 30px;
        margin-top: 20px;
        padding-top: 15px;
      }

      .sig-box { 
        text-align: center; 
        padding: 15px;
        border: 1px dashed #bf4f1f;
        border-radius: 4px;
        background: #fdf6f3;
      }
      
      .sig-title { font-weight: bold; color: #bf4f1f; font-size: 12px; margin-bottom: 35px; }
      .sig-line { border-top: 1px solid #bf4f1f; margin: 0 25px; }
      .sig-hint { font-size: 8px; color: #888; margin-top: 5px; }
    </style>

    <div class="print-container">
      ${createPage(page1Content, 1, 2)}
      ${createPage(page2Content, 2, 2)}
    </div>
  `;

  document.body.appendChild(printContent);

  try {
    await new Promise(resolve => setTimeout(resolve, 300));

    const pages = printContent.querySelectorAll('.page');
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123
      });

      const imgData = canvas.toDataURL('image/png');
      
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    }

    const pdfBlob = pdf.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } else {
      pdf.save(`BrokerageAgreement_${agreementId}.pdf`);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    document.body.removeChild(printContent);
  }
};